import { Search, Upload, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";

import { useTrackLibrary } from "../../api/hooks.js";
import { Button } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { formatDate, formatDuration } from "../../lib/format.js";
import { Artwork } from "../../player/artwork.js";
import { PageHeading } from "../shared/page-heading.js";
import { TrackEditor } from "./track-editor.js";
import {
  isSettled,
  QUEUE_STATE_LABELS,
  useUploadQueue,
  type QueueEntry,
} from "./upload-queue.js";
import type { Track } from "../../api/types.js";

/**
 * The library: one pool, and the whole of it.
 *
 * There is no feed on this screen and no way to reach one from it. That is the point
 * — a track belongs to the library, and which feeds happen to play it is a fact about
 * those feeds. Anything here that offered to scope the library to a feed would be
 * building the wall the primitives forbid.
 *
 * It is a list rather than a table of pages: a library is browsed and searched, not
 * paged through like search results. The API pages it, the screen appends, and the
 * search goes to the API so that it searches the library rather than the part of it
 * that happens to be loaded.
 */
export function LibraryPage() {
  const [query, setQuery] = useState("");
  const search = useDebounced(query, 250);
  const library = useTrackLibrary(search);
  const [editing, setEditing] = useState<Track | null>(null);
  const [edits, setEdits] = useState<Record<string, Track>>({});
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [justAdded, setJustAdded] = useState<Track[]>([]);
  const [dragging, setDragging] = useState(false);
  const filePicker = useRef<HTMLInputElement | null>(null);

  const queue = useUploadQueue(
    useCallback((track: Track) => {
      setJustAdded((current) =>
        current.some((entry) => entry.id === track.id)
          ? current
          : [track, ...current],
      );
    }, []),
  );

  /**
   * What was added in this session sits at the top until the list is re-read. The API
   * orders the library by when a track was added, so a new upload lands on the last
   * page of a large library — which is exactly where the operator is not looking.
   */
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const all: Track[] = [];
    for (const track of [...justAdded, ...library.items]) {
      const current = edits[track.id] ?? track;
      if (seen.has(current.id) || removed.has(current.id)) continue;
      seen.add(current.id);
      all.push(current);
    }
    return all;
  }, [edits, justAdded, library.items, removed]);

  const matching = useMemo(() => {
    if (search.trim() === "") return rows;
    // Everything from the API already matches; only the locally pinned additions
    // have to be held to the same question.
    const needle = search.trim().toLowerCase();
    const fromApi = new Set(library.items.map((track) => track.id));
    return rows.filter(
      (track) =>
        fromApi.has(track.id) ||
        `${track.title} ${track.artist} ${track.album}`
          .toLowerCase()
          .includes(needle),
    );
  }, [library.items, rows, search]);

  function accept(files: FileList | null) {
    const audio = [...(files ?? [])];
    if (audio.length > 0) queue.add(audio);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    accept(event.dataTransfer.files);
  }

  return (
    <div
      className="relative flex flex-col gap-6"
      onDragEnter={(event) => {
        if (event.dataTransfer.types.includes("Files")) setDragging(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) setDragging(false);
      }}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) event.preventDefault();
      }}
      onDrop={onDrop}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeading
          subtitle="Every track your feeds can play."
          title="Library"
        />
        <input
          accept="audio/*"
          className="hidden"
          multiple
          onChange={(event) => {
            accept(event.target.files);
            event.target.value = "";
          }}
          ref={filePicker}
          type="file"
        />
        <Button onClick={() => filePicker.current?.click()}>
          <Upload aria-hidden="true" />
          Upload files
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3"
          />
          <input
            aria-label="Search the library"
            className="min-h-11 w-full rounded-sm border border-border bg-bg-2 pr-9 pl-9 text-sm text-ink transition-colors placeholder:text-ink-3 hover:border-border-hi focus:border-accent-line focus:outline-none"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Search title, artist or album"
            type="search"
            value={query}
          />
          {query !== "" ? (
            <button
              aria-label="Clear the search"
              className="absolute top-1/2 right-2 grid size-7 -translate-y-1/2 place-items-center rounded-sm text-ink-3 hover:bg-surface-hi hover:text-ink"
              onClick={() => {
                setQuery("");
              }}
              type="button"
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          ) : null}
        </div>
        <p className="font-mono text-[0.6875rem] tracking-[0.04em] text-ink-3">
          {library.loading
            ? "Reading the library"
            : `${String(matching.length)} loaded${library.hasMore ? " · more to read" : ""}`}
        </p>
      </div>

      {queue.entries.length > 0 ? (
        <UploadPanel
          entries={queue.entries}
          onClear={queue.clearSettled}
          running={queue.running}
        />
      ) : null}

      {library.loading ? (
        <StateSurface kind="loading" title="Reading the library" />
      ) : library.error ? (
        <StateSurface
          action={
            <Button onClick={library.reload} size="small" variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title="The library could not be read"
        >
          {library.error}
        </StateSurface>
      ) : matching.length === 0 ? (
        <StateSurface
          action={
            search.trim() === "" ? (
              <Button onClick={() => filePicker.current?.click()} size="small">
                <Upload aria-hidden="true" />
                Add music
              </Button>
            ) : null
          }
          kind="empty"
          title={search.trim() === "" ? "No music yet" : "Nothing matches"}
        >
          {search.trim() === ""
            ? "Drop files anywhere on this page, or choose them. Every feed draws on what is here."
            : "No track in the library has that in its title, artist or album."}
        </StateSurface>
      ) : (
        <>
          <TrackTable
            justAddedIds={new Set(justAdded.map((track) => track.id))}
            onOpen={setEditing}
            tracks={matching}
          />
          {library.hasMore ? (
            <LoadMore
              busy={library.loadingMore}
              onLoadMore={library.loadMore}
            />
          ) : (
            <p className="pb-2 text-center font-mono text-[0.6875rem] text-ink-3">
              End of the library
            </p>
          )}
        </>
      )}

      {dragging ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-40 grid place-items-center bg-bg/80 backdrop-blur-[2px]"
        >
          <div className="rounded-md border-2 border-dashed border-accent-line bg-surface px-8 py-6 text-center">
            <Upload
              aria-hidden="true"
              className="mx-auto size-6 text-accent-text"
            />
            <p className="mt-2 font-semibold text-ink">
              Drop to add to the library
            </p>
          </div>
        </div>
      ) : null}

      <TrackEditor
        onChanged={(track) => {
          setEdits((current) => ({ ...current, [track.id]: track }));
          setEditing(track);
        }}
        onClose={() => {
          setEditing(null);
        }}
        onDeleted={(trackId) => {
          setRemoved((current) => new Set(current).add(trackId));
        }}
        track={editing}
      />
    </div>
  );
}

/**
 * The dense list. At 1440 it is a table with alignable columns, because a library is
 * read down a column; at 360 the same row stacks, because four columns in 360px is
 * four columns of nothing.
 */
function TrackTable({
  justAddedIds,
  onOpen,
  tracks,
}: {
  justAddedIds: ReadonlySet<string>;
  onOpen: (track: Track) => void;
  tracks: readonly Track[];
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface">
      <div className="hidden items-center gap-4 border-b border-border px-4 py-2.5 font-mono text-[0.625rem] tracking-[0.12em] text-ink-3 uppercase lg:flex">
        <span className="w-10" />
        <span className="min-w-0 flex-1">Title</span>
        <span className="w-48 shrink-0">Album</span>
        <span className="w-24 shrink-0">Genre</span>
        <span className="w-16 shrink-0 text-right">Length</span>
        <span className="w-24 shrink-0 text-right">Added</span>
      </div>
      <ul>
        {tracks.map((track) => (
          <li key={track.id} className="border-b border-border last:border-b-0">
            <button
              className="flex w-full items-center gap-4 px-4 py-2.5 text-left transition-colors hover:bg-surface-hi"
              onClick={() => {
                onOpen(track);
              }}
              type="button"
            >
              <Artwork
                className="size-10 rounded-sm"
                markClassName="size-1/2"
                src={
                  track.artworkMediaId
                    ? `/api/artwork/${encodeURIComponent(track.id)}?v=${encodeURIComponent(track.artworkMediaId)}`
                    : null
                }
              />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium text-ink">
                    {track.title}
                  </span>
                  {justAddedIds.has(track.id) ? (
                    <span className="shrink-0 rounded-full bg-accent-soft px-1.5 py-0.5 font-mono text-[0.5625rem] tracking-[0.08em] text-accent-text uppercase">
                      Just added
                    </span>
                  ) : null}
                </span>
                <span className="truncate text-xs text-ink-3">
                  {track.artist || "Unknown artist"}
                  <span className="lg:hidden">
                    {track.album ? ` · ${track.album}` : ""}
                    {` · ${formatDuration(track.durationMs)}`}
                  </span>
                </span>
              </span>
              <span className="hidden w-48 shrink-0 truncate text-xs text-ink-2 lg:block">
                {track.album || "—"}
              </span>
              <span className="hidden w-24 shrink-0 truncate text-xs text-ink-3 lg:block">
                {track.genre || "—"}
              </span>
              <span className="hidden w-16 shrink-0 text-right font-mono text-xs text-ink-2 tabular-nums lg:block">
                {formatDuration(track.durationMs)}
              </span>
              <span className="hidden w-24 shrink-0 text-right font-mono text-[0.6875rem] text-ink-3 lg:block">
                {formatDate(track.createdAt)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The end of what has been read. It loads the next page when it comes into view, and
 * is a button as well — an operator who has scrolled to the end should not have to
 * discover that scrolling further is the gesture.
 */
function LoadMore({
  busy,
  onLoadMore,
}: {
  busy: boolean;
  onLoadMore: () => void;
}) {
  const sentinel = useRef<HTMLDivElement | null>(null);
  const load = useRef(onLoadMore);
  load.current = onLoadMore;

  useEffect(() => {
    const element = sentinel.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) load.current();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="flex justify-center pb-2" ref={sentinel}>
      <Button
        disabled={busy}
        onClick={onLoadMore}
        size="small"
        variant="secondary"
      >
        {busy ? "Reading…" : "Load more"}
      </Button>
    </div>
  );
}

const SETTLED_TONE: Record<string, string> = {
  added: "text-ok",
  "already-in-library": "text-ink-3",
  failed: "text-err",
};

/** What is happening to the files that were dropped, and what the API said. */
function UploadPanel({
  entries,
  onClear,
  running,
}: {
  entries: readonly QueueEntry[];
  onClear: () => void;
  running: boolean;
}) {
  const done = entries.filter((entry) => isSettled(entry.state)).length;
  return (
    <section className="rounded-md border border-border bg-surface">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <h2 className="text-sm font-semibold text-ink">
          {running
            ? `Adding ${String(entries.length - done)} of ${String(entries.length)}`
            : `${String(entries.length)} handled`}
        </h2>
        {done > 0 ? (
          <Button onClick={onClear} size="small" variant="ghost">
            Clear finished
          </Button>
        ) : null}
      </header>
      <ul className="max-h-64 overflow-y-auto">
        {entries.map((entry) => (
          <li
            key={entry.key}
            className="flex items-center gap-3 border-b border-border px-4 py-2 last:border-b-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-ink">
                {entry.title}
              </span>
              <span className="block truncate text-xs text-ink-3">
                {entry.artist || entry.fileName}
                {entry.detail ? ` · ${entry.detail}` : ""}
              </span>
            </span>
            <span
              className={cn(
                "shrink-0 font-mono text-[0.6875rem] whitespace-nowrap",
                SETTLED_TONE[entry.state] ?? "text-ink-2",
              )}
            >
              {QUEUE_STATE_LABELS[entry.state]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A search box that does not ask the API a question per keystroke. */
function useDebounced(value: string, delayMs: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [delayMs, value]);
  return settled;
}
